<template>
  <MainLayout>
    <!-- Toolbar -->
    <template v-slot:toolbar>
      <div class="page-title d-flex flex-column justify-content-center flex-wrap me-3">
        <h1 class="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
          Detalhes do Histórico de Alteração
        </h1>
        <ul class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 pt-1">
          <li class="breadcrumb-item text-muted">
            <router-link to="/change-history" class="text-muted text-hover-primary">
              Histórico de alterações
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <span class="bullet bg-gray-400 w-5px h-2px"></span>
          </li>
          <li class="breadcrumb-item text-muted">Detalhes</li>
        </ul>
      </div>
      <div class="d-flex align-items-center gap-2 gap-lg-3">
        <router-link to="/change-history" class="btn btn-sm btn-secondary">
          <i class="fas fa-arrow-left"></i>
          Voltar
        </router-link>
      </div>
    </template>

    <!-- Content -->
    <template v-if="loader">
      <div class="d-flex justify-content-center py-10">
        <Loader size="big" />
      </div>
    </template>

    <template v-else-if="!changeLog">
      <div class="alert alert-danger">
        <h4>Erro</h4>
        <p>Não foi possível carregar os detalhes do histórico de alteração.</p>
      </div>
    </template>

    <template v-else>
      <!-- Basic Information and Action Details -->
      <div class="row g-6 g-xl-9">
        <!-- Basic Information Card -->
        <div class="col-xl-6">
          <div class="card card-flush h-xl-100">
            <div class="card-header align-content-center rounded bgi-no-repeat bgi-size-cover bgi-position-y-top bgi-position-x-center align-items-start"
                 style="background-color: #1e1e2d;">
              <h3 class="card-title align-items-start flex-column">
                <span class="fw-bold fs-3 mb-1 text-white">Informações Básicas</span>
              </h3>
            </div>
            <div class="card-body mt-n20">
              <div class="mt-20 position-relative">
                <div class="row g-3 g-lg-6">
                  <div class="col-6">
                    <div class="bg-gray-100 bg-opacity-70 rounded-2 px-6 py-5">
                      <div class="m-0">
                        <i class="fas fa-calendar-alt text-primary fs-1 mb-3"></i>
                        <span class="text-gray-700 fw-bolder d-block fs-2qx lh-1">
                          {{ changeLog.created_at | formatDate('DD/MM/YYYY') }}
                        </span>
                        <span class="text-gray-500 fw-semibold fs-6">
                          {{ changeLog.created_at | formatDate('HH:mm:ss') }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="bg-gray-100 bg-opacity-70 rounded-2 px-6 py-5">
                      <div class="m-0">
                        <i class="fas fa-user text-success fs-1 mb-3"></i>
                        <span class="text-gray-700 fw-bolder d-block fs-2qx lh-1">
                          {{ changeLog.user_name || 'Sistema' }}
                        </span>
                        <span class="text-gray-500 fw-semibold fs-6">Usuário</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Impersonate Info -->
                <div v-if="changeLog.type === 'IMPERSONATE'" class="mt-5">
                  <div class="alert alert-warning d-flex align-items-center p-5">
                    <i class="fas fa-user-secret fs-2x text-warning me-4"></i>
                    <div class="d-flex flex-column">
                      <h4 class="mb-1 text-warning">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        Ação realizada via Impersonate
                      </h4>
                      <span class="fs-6">
                        <strong>{{ changeLog.user_name }}</strong> estava impersonando
                        <strong>{{ changeLog.impersonating_user_name }}</strong> quando esta ação foi realizada
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else class="mt-5">
                  <div class="alert alert-primary d-flex align-items-center p-5">
                    <i class="fas fa-check fs-2x text-primary me-4"></i>
                    <div class="d-flex flex-column">
                      <h4 class="mb-1 text-primary">
                        <i class="me-2"></i>
                        Ação realizada pelo usuário acima, sem o uso de impersonate.
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Details Card -->
        <div class="col-xl-6">
          <div class="card card-flush h-xl-100">
            <div class="card-header pt-7">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1">Detalhes da Ação</span>
              </h3>
            </div>
            <div class="card-body pt-6">
              <!-- Entity Type -->
              <div class="d-flex flex-wrap align-items-center mb-7">
                <div class="col-6 d-flex flex-column flex-grow-1 my-lg-0 my-2 pe-3">
                  <span class="text-gray-800 fw-bold fs-6">Tipo de Entidade</span>
                  <span class="text-gray-400 fw-semibold fs-7">A entidade que foi modificada</span>
                </div>
                <div class="col-6 align-content-end align-content-end" style="text-align: right;">
                  <span class="badge fs-8 fw-bold" :class="getEntityTypeBadgeClass(changeLog.loggable_type)">
                    {{ $options.filters.formatEntityNames(changeLog.loggable_type) }}
                  </span>
                </div>
              </div>

              <!-- Action Performed -->
              <div class="flex-wrap align-items-start mb-7">
                <div class="d-flex flex-column flex-grow-1 my-lg-0 my-2 pe-3">
                  <span class="text-gray-800 fw-bold fs-6">Ação Realizada</span>
                  <span class="text-gray-400 fw-semibold fs-7">O que foi feito com a entidade</span>
                </div>
                <div class="d-flex flex-column align-items-end flex-grow-1">
                  <span class="badge fs-8 fw-bold mb-3" :class="getActionBadgeClass(changeLog.type)">
                    {{ formatChangeAction(changeLog) }}
                  </span>

                  <!-- Detailed Changes -->
                  <div v-if="hasDetailedChanges && !changeLog.type.includes('REPLICATE')" class="w-100">
                    <div v-for="(change, field) in changeLog.changes" :key="field" class="mb-3">
                      <div class="d-flex align-items-start border-start border-3 border-primary ps-3 py-2">
                        <div class="flex-grow-1">
                          <div class="fw-bold text-gray-800 fs-7 mb-1">
                            <i class="fas fa-edit text-primary me-1"></i>
                            {{ formatFieldName(change.field_key) }}
                          </div>
                          <div class="d-flex flex-column gap-1">
                            <div class="d-flex align-items-center">
                              <span class="badge badge-light-danger badge-sm me-2">Antes</span>
                              <span class="text-gray-600 fs-8">{{ formatFieldValue(field, change.old) }}</span>
                            </div>
                            <div class="d-flex align-items-center">
                              <span class="badge badge-light-success badge-sm me-2">Depois</span>
                              <span class="text-gray-800 fs-8 fw-semibold">{{ formatFieldValue(field, change.new) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <template v-else>
                    <span class="text-gray-500 fs-8 fst-italic">Nenhuma alteração detalhada disponível</span>
                  </template>
                </div>
              </div>

              <!-- Entity Name -->
              <div v-if="changeLog.loggable" class="d-flex flex-wrap align-items-center mb-7">
                <div class="d-flex flex-column flex-grow-1 my-lg-0 my-2 pe-3">
                  <span class="text-gray-800 fw-bold fs-6">Nome da Entidade</span>
                  <span class="text-gray-400 fw-semibold fs-7">Nome/título da entidade afetada</span>
                </div>
                <span class="text-gray-800 fw-bold fs-6">
                  {{ changeLog.loggable.name || 'N/A' }}
                </span>
              </div>

              <!-- Entity ID -->
              <div class="d-flex flex-wrap align-items-center mb-7">
                <div class="d-flex flex-column flex-grow-1 my-lg-0 my-2 pe-3">
                  <span class="text-gray-800 fw-bold fs-6">ID da Entidade</span>
                  <span class="text-gray-400 fw-semibold fs-7">Identificador único da entidade</span>
                </div>
                <span class="text-gray-800 fw-bold fs-6">
                  {{ changeLog.loggable_id }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Changes Details Card -->
      <div v-if="changeLog.old_entity || changeLog.new_entity" class="card card-flush mt-6">
        <div class="card-header pt-7">
          <h3 class="card-title align-items-start flex-column">
            <span class="card-label fw-bold fs-3 mb-1">Alterações</span>
            <span class="text-gray-400 mt-1 fw-semibold fs-6">Comparação entre valores antigo e novo</span>
          </h3>
        </div>
        <div class="card-body pt-6">
          <div class="row g-6">
            <!-- Old Value -->
            <div v-if="changeLog.old_entity" class="col-lg-6">
              <div class="card border-dashed border-danger h-100">
                <div class="card-header border-0 pt-6">
                  <div class="card-title">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-history text-danger me-2"></i>
                      <span class="fs-6 fw-bold text-gray-800">Valor Anterior</span>
                    </div>
                  </div>
                </div>
                <div class="card-body pt-0">
                  <div class="bg-light-danger rounded p-4">
                    <pre class="mb-0 text-gray-700 fs-7">{{ formatValue(changeLog.old_entity) }}</pre>
                  </div>
                </div>
              </div>
            </div>

            <!-- New Value -->
            <div v-if="changeLog.new_entity || changeLog.request" class="col-lg-6">
              <div class="card border-dashed border-success h-100">
                <div class="card-header border-0 pt-6">
                  <div class="card-title">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-check text-success me-2"></i>
                      <span class="fs-6 fw-bold text-gray-800">{{ changeLog.new_entity ? 'Valor Novo' : 'Valor Enviado'}}</span>
                    </div>
                  </div>
                </div>
                <div class="card-body pt-0">
                  <div class="bg-light-success rounded p-4">
                    <pre v-if="changeLog.new_entity" class="mb-0 text-gray-700 fs-7">{{ formatValue(changeLog.new_entity) }}</pre>
                    <pre v-else-if="changeLog.request" class="mb-0 text-gray-700 fs-7">{{ formatValue(changeLog.request) }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import ChangeHistoryService from '@/modules/change-history/services/change-history-service'
import changeFormatterMixin from '@/modules/change-history/mixins/change-formatter-mixin'
import Loader from '@/components/common/Loader/Loader'
import MainLayout from '@/components/layouts/MainLayout'

export default {
  name: 'ChangeHistoryShow',

  components: {
    Loader,
    MainLayout
  },

  mixins: [changeFormatterMixin],

  metaInfo () {
    return {
      title: 'Detalhes do Histórico de Alteração'
    }
  },

  data () {
    return {
      changeLog: null,
      loader: false
    }
  },
  computed: {
    /**
     * Check if there are detailed changes to display
     */
    hasDetailedChanges () {
      return this.changeLog?.changes && Object.keys(this.changeLog.changes).length > 0
    }
  },
  created () {
    this.loadChangeLog()
  },
  methods: {
    /**
     * Loads change log data
     */
    async loadChangeLog () {
      this.loader = true
      try {
        const uuid = this.$route.params.uuid
        this.changeLog = await ChangeHistoryService.getChangeLogDetails(uuid)
        console.log(this.changeLog)
      } catch (error) {
        this.handleError(error)
      }
      this.loader = false
    },

    /**
     * Handles API errors
     */
    handleError (error) {
      if (error.handleGlobally) {
        error.handleGlobally()
      }
    },

    /**
     * Formats field name for display
     */
    formatFieldName (field) {
      console.log(field)

      const fieldNames = {
        name: 'Nome',
        browser: 'Navegador',
        environment: 'Ambiente',
        demographics: 'Demografia',
        status: 'Status',
        amount: 'Valor',
        start_date: 'Data de Início',
        end_date: 'Data de Término',
        type: 'Tipo',
        frequency_cap: 'Limite de Frequência',
        cities: 'Cidades',
        states: 'Estados',
        keywords: 'Palavras-chave',
        audiences: 'Audiências',
        categories: 'Categorias',
        creatives: 'Criativos'
      }
      return fieldNames[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ')
    },

    /**
     * Formats field value for display
     */
    formatFieldValue (field, value) {
      if (value === null || value === undefined || value === '') {
        return 'N/A'
      }

      // Handle array values
      if (Array.isArray(value)) {
        if (value.length === 0) return 'Nenhum'
        return value.join(', ')
      }

      // Handle object values (like demographics)
      if (typeof value === 'object') {
        return this.formatObjectValue(field, value)
      }

      // Handle string values with commas (like "MOBILE,DESKTOP")
      if (typeof value === 'string' && value.includes(',')) {
        return value.split(',').join(', ')
      }

      return String(value)
    },

    /**
     * Formats object values (like demographics)
     */
    formatObjectValue (field, obj) {
      if (field === 'demographics') {
        const parts = []
        if (obj.gender) {
          const genderMap = { MALE: 'Masculino', FEMALE: 'Feminino', OTHER: 'Outro' }
          const genders = obj.gender.map(g => genderMap[g] || g)
          parts.push(`Gênero: ${genders.join(', ')}`)
        }
        if (obj.age) {
          const ages = obj.age.map(a => a === 'UNKNOWN' ? 'Desconhecido' : a)
          parts.push(`Idade: ${ages.join('-')}`)
        }
        return parts.join(' | ')
      }

      // For other objects, show as JSON
      try {
        return JSON.stringify(obj, null, 0)
      } catch {
        return String(obj)
      }
    },

    /**
     * Gets entity type badge class
     */
    getEntityTypeBadgeClass (entityType) {
      const badgeClasses = {
        ADVERTISER: 'badge-primary',
        CAMPAIGN: 'badge-info',
        CAMPAIGN_PACKAGE: 'badge-secondary',
        CAMPAIGN_LINE: 'badge-warning',
        CREATIVE: 'badge-success',
        CUSTOM_TARGETING: 'badge-dark',
        USER: 'badge-light',
        ORDER: 'badge-danger',
        INSERTION_ORDER: 'badge-warning text-dark'
      }
      return badgeClasses[entityType] ? { [badgeClasses[entityType]]: true } : {}
    },

    /**
     * Gets action badge class
     */
    getActionBadgeClass (action) {
      return {
        'badge-success': action.includes('CREATE') || action.includes('ACTIVATE') || action.includes('APPROVE') || action.includes('ISSUE'),
        'badge-warning': action.includes('UPDATE'),
        'badge-danger': action.includes('DELETE') || action.includes('DESTROY') || action.includes('PAUSE'),
        'badge-info': action.includes('IMPORT') || action.includes('REPLICATE') || action.includes('IMPERSONATE')
      }
    },

    /**
     * Formats value for display
     */
    formatValue (value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }

      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2)
      }

      return String(value)
    }
  }
}
</script>
